import {PropsWithChildren, ReactNode} from "react";

export type AdminPageProps = PropsWithChildren<{
  eyebrow?: ReactNode;
  title: ReactNode;
  status?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  /** Inline day/navigation controls beside the title in a workspace header. */
  headerControls?: ReactNode;
  /** Centers workspace controls in their own column; smaller hosts place them on a centered row. */
  headerControlsPlacement?: "inline" | "center";
  /** A single compact, wrapping control row for screens whose content owns scrolling. */
  headerLayout?: "default" | "workspace";
  className?: string;
  contentClassName?: string;
  /** Removes the standard content-width cap for dense operational workspaces. */
  fullWidth?: boolean;
  /** Makes the page and its content column fill the host height so a child panel can own scrolling. */
  layout?: "document" | "fill";
  density?: "compact" | "default";
}>;

export const AdminPage = ({
  eyebrow,
  title,
  status,
  subtitle,
  actions,
  headerControls,
  headerControlsPlacement = "inline",
  headerLayout = "default",
  className,
  contentClassName,
  fullWidth = false,
  layout = "document",
  density = "default",
  children,
}: AdminPageProps) => {
  const centeredControls = headerLayout === "workspace" && headerControlsPlacement === "center";
  return (
    <main
      className={[
        "box-border w-full min-w-0 max-w-full bg-[var(--ots-background)]",
        centeredControls ? "@container" : "",
        headerLayout === "workspace"
          ? "p-2 sm:p-3"
          : density === "compact"
          ? "px-3 py-4"
          : "px-4 py-7 sm:px-6 sm:py-9 lg:px-8 lg:py-10",
        layout === "fill" ? "!flex h-full min-h-0 flex-col overflow-hidden" : "min-h-full",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div
        className={[
          "mx-auto min-w-0",
          fullWidth ? "w-full max-w-none" : "w-full max-w-[1340px]",
          layout === "fill" ? "flex min-h-0 flex-1 flex-col" : "",
          contentClassName,
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {/* Wraps instead of switching at a viewport breakpoint: a page mounted in a narrow GWT panel
          on a wide screen then gets its actions under the title, not a title squeezed to a sliver. */}
        <header
          className={[
            centeredControls
              ? "shrink-0 !grid grid-cols-[minmax(0,1fr)] @md:grid-cols-[minmax(0,1fr)_auto] @5xl:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]"
              : "shrink-0 !flex flex-wrap justify-between",
            headerLayout === "workspace" ? "items-center gap-x-4 gap-y-2" : "items-start",
            headerLayout === "workspace" ? "" : density === "compact" ? "gap-3" : "gap-6",
          ].join(" ")}
        >
          <div
            className={
              headerLayout === "workspace"
                ? "flex min-w-0 flex-wrap items-center gap-x-4 gap-y-2"
                : "min-w-[min(100%,20rem)] flex-1"
            }
          >
            <div>
              {eyebrow && (
                <div
                  className={["text-xs text-[var(--ots-text-muted)]", density === "compact" ? "mb-1.5" : "mb-3"].join(
                    " "
                  )}
                >
                  {eyebrow}
                </div>
              )}
              <div className="flex flex-wrap items-center gap-3">
                <h1
                  className={[
                    "font-[family-name:var(--ots-font-display)] font-semibold tracking-[-0.02em] text-[var(--ots-secondary)]",
                    headerLayout === "workspace"
                      ? "!m-0 !text-lg !leading-8"
                      : density === "compact"
                      ? "text-xl"
                      : "text-2xl sm:text-[30px]",
                  ].join(" ")}
                >
                  {title}
                </h1>
                {status}
              </div>
              {subtitle && (
                <div
                  className={[
                    "max-w-3xl text-[var(--ots-text-muted)]",
                    density === "compact" ? "mt-1.5 text-xs leading-5" : "mt-3 text-sm leading-6",
                  ].join(" ")}
                >
                  {subtitle}
                </div>
              )}
            </div>
            {!centeredControls && headerControls}
          </div>
          {centeredControls && (
            <div className="col-start-1 row-start-2 flex min-w-0 justify-center @md:col-span-2 @5xl:col-span-1 @5xl:col-start-2 @5xl:row-start-1">
              {headerControls}
            </div>
          )}
          {actions && (
            <div
              className={[
                centeredControls
                  ? "row-start-3 min-w-0 justify-self-end @md:col-start-2 @md:row-start-1 @5xl:col-start-3"
                  : headerLayout === "workspace"
                  ? "ml-auto min-w-0"
                  : "",
                "flex flex-wrap items-center gap-2",
              ].join(" ")}
            >
              {actions}
            </div>
          )}
        </header>
        {children}
      </div>
    </main>
  );
};
